// ============================================================
// Pieces — Movement vectors and cross-dimensional rules
// ============================================================

import { type PieceKind, type Position, NUM_PLANES, BOARD_SIZE } from './types';

// --------------- Movement Vectors ---------------

/** Direction vector in 3D (dx, dy, dw) */
export interface MoveVector {
  dx: number;
  dy: number;
  dw: number;
}

/** Whether a piece slides (repeats vector) or steps (single application) */
export type MoveStyle = 'slide' | 'step';

export interface MoveDef {
  vectors: MoveVector[];
  style: MoveStyle;
}

// Same-plane movement (dw=0 always)
const ROOK_VECTORS: MoveVector[] = [
  { dx: 1, dy: 0, dw: 0 }, { dx: -1, dy: 0, dw: 0 },
  { dx: 0, dy: 1, dw: 0 }, { dx: 0, dy: -1, dw: 0 },
];

const BISHOP_VECTORS: MoveVector[] = [
  { dx: 1, dy: 1, dw: 0 }, { dx: 1, dy: -1, dw: 0 },
  { dx: -1, dy: 1, dw: 0 }, { dx: -1, dy: -1, dw: 0 },
];

const QUEEN_VECTORS: MoveVector[] = [...ROOK_VECTORS, ...BISHOP_VECTORS];

const KNIGHT_VECTORS: MoveVector[] = [
  { dx: 2, dy: 1, dw: 0 }, { dx: 2, dy: -1, dw: 0 },
  { dx: -2, dy: 1, dw: 0 }, { dx: -2, dy: -1, dw: 0 },
  { dx: 1, dy: 2, dw: 0 }, { dx: 1, dy: -2, dw: 0 },
  { dx: -1, dy: 2, dw: 0 }, { dx: -1, dy: -2, dw: 0 },
];

const KING_VECTORS: MoveVector[] = [
  { dx: 1, dy: 0, dw: 0 }, { dx: -1, dy: 0, dw: 0 },
  { dx: 0, dy: 1, dw: 0 }, { dx: 0, dy: -1, dw: 0 },
  { dx: 1, dy: 1, dw: 0 }, { dx: 1, dy: -1, dw: 0 },
  { dx: -1, dy: 1, dw: 0 }, { dx: -1, dy: -1, dw: 0 },
];

/** Same-plane movement definitions */
export const PIECE_MOVES: Record<PieceKind, MoveDef> = {
  pawn: { vectors: [], style: 'step' },     // special-cased
  knight: { vectors: KNIGHT_VECTORS, style: 'step' },
  bishop: { vectors: BISHOP_VECTORS, style: 'slide' },
  rook: { vectors: ROOK_VECTORS, style: 'slide' },
  queen: { vectors: QUEEN_VECTORS, style: 'slide' },
  king: { vectors: KING_VECTORS, style: 'step' },
};

// --------------- Cross-Dimensional Vectors ---------------

/**
 * Cross-dimensional movement rules per piece type.
 * Each returns an array of target positions given the source.
 */

/** Pawn: ±1 W, must also move +1 Y (forward) */
export function pawnCrossDim(from: Position, forward: number): Position[] {
  const targets: Position[] = [];
  for (const dw of [-1, 1]) {
    const w = from.w + dw;
    const y = from.y + forward;
    if (w >= 0 && w < NUM_PLANES && y >= 0 && y < BOARD_SIZE) {
      // Straight cross-dim advance
      targets.push({ x: from.x, y, w });
      // Diagonal cross-dim captures
      if (from.x - 1 >= 0) targets.push({ x: from.x - 1, y, w });
      if (from.x + 1 < BOARD_SIZE) targets.push({ x: from.x + 1, y, w });
    }
  }
  return targets;
}

/** Knight: ±1 or ±2 W as part of L-shape */
export function knightCrossDim(from: Position): Position[] {
  const targets: Position[] = [];
  // L extends into W: (±2 W, ±1 x/y) and (±1 W, ±2 x/y)
  for (const dw of [-2, -1, 1, 2]) {
    const w = from.w + dw;
    if (w < 0 || w >= NUM_PLANES) continue;
    const spatialStep = Math.abs(dw) === 2 ? 1 : 2;
    for (const [dx, dy] of [[spatialStep, 0], [-spatialStep, 0], [0, spatialStep], [0, -spatialStep]]) {
      const x = from.x + dx;
      const y = from.y + dy;
      if (x >= 0 && x < BOARD_SIZE && y >= 0 && y < BOARD_SIZE) {
        targets.push({ x, y, w });
      }
    }
  }
  return targets;
}

/** Bishop: ±1 W per diagonal step (slide extends into W) */
export function bishopCrossDim(from: Position): Position[] {
  const targets: Position[] = [];
  for (const dw of [-1, 1]) {
    for (const dx of [-1, 1]) {
      for (const dy of [-1, 1]) {
        // Slide through W while sliding diagonally
        let x = from.x + dx;
        let y = from.y + dy;
        let w = from.w + dw;
        while (x >= 0 && x < BOARD_SIZE && y >= 0 && y < BOARD_SIZE && w >= 0 && w < NUM_PLANES) {
          targets.push({ x, y, w });
          x += dx;
          y += dy;
          w += dw;
        }
      }
    }
  }
  return targets;
}

/** Rook: straight along W-axis (phase through dimensions) */
export function rookCrossDim(from: Position): Position[] {
  const targets: Position[] = [];
  // Pure W-axis movement (rook's unique power)
  for (let w = 0; w < NUM_PLANES; w++) {
    if (w !== from.w) {
      targets.push({ x: from.x, y: from.y, w });
    }
  }
  // Also: slide along rank/file while shifting W
  for (const dw of [-1, 1]) {
    for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
      let x = from.x + dx;
      let y = from.y + dy;
      let w = from.w + dw;
      while (x >= 0 && x < BOARD_SIZE && y >= 0 && y < BOARD_SIZE && w >= 0 && w < NUM_PLANES) {
        targets.push({ x, y, w });
        x += dx;
        y += dy;
        w += dw;
      }
    }
  }
  return targets;
}

/** Queen: combines bishop and rook cross-dim */
export function queenCrossDim(from: Position): Position[] {
  return [...bishopCrossDim(from), ...rookCrossDim(from)];
}

/** King: ±1 W only */
export function kingCrossDim(from: Position): Position[] {
  const targets: Position[] = [];
  for (const dw of [-1, 1]) {
    const w = from.w + dw;
    if (w >= 0 && w < NUM_PLANES) {
      // King can move ±1 in any spatial direction when crossing dims
      for (let dx = -1; dx <= 1; dx++) {
        for (let dy = -1; dy <= 1; dy++) {
          const x = from.x + dx;
          const y = from.y + dy;
          if (x >= 0 && x < BOARD_SIZE && y >= 0 && y < BOARD_SIZE) {
            targets.push({ x, y, w });
          }
        }
      }
    }
  }
  return targets;
}
